import { inject, Injectable } from '@angular/core';
import { Observable, catchError, from, Subject, shareReplay, throwError } from 'rxjs';
import {
  RolesApi,
  RoleList,
  Role,
  CreateRole,
  UpdateRole,
  GrantsApi,
  GrantList,
} from '@tourfold/libraries/tf-api-client-public-v2';

@Injectable({
  providedIn: 'root',
})
export class RolesService {
  private readonly rolesApi = inject(RolesApi);
  private readonly grantsApi = inject(GrantsApi);
  private readonly rolesUpdated = new Subject<void>();
  private rolesCache$?: Observable<RoleList>;

  readonly rolesUpdated$ = this.rolesUpdated.asObservable();

  getRoles(search?: string): Observable<RoleList> {
    if (!search && this.rolesCache$) {
      return this.rolesCache$;
    }
    const request$ = from(this.rolesApi.getRoles({ search: search ?? undefined })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
    if (!search) {
      this.rolesCache$ = request$.pipe(shareReplay(1));
      return this.rolesCache$;
    }
    return request$;
  }

  getRoleById(roleId: string): Observable<Role> {
    return from(this.rolesApi.getRoleById({ roleId }));
  }

  createRole(payload: CreateRole): Observable<Role> {
    return from(this.rolesApi.createRole({ createRole: payload }));
  }

  updateRole(roleId: string, payload: UpdateRole): Observable<Role> {
    return from(this.rolesApi.replaceRole({ roleId, updateRole: payload }));
  }

  deleteRole(roleId: string): Observable<void> {
    return from(this.rolesApi.deleteRole({ roleId }));
  }

  getGrants(): Observable<GrantList> {
    return from(this.grantsApi.getGrants()).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  notifyRolesUpdated(): void {
    this.rolesCache$ = undefined; // Clear cache so next getRoles() fetches fresh data
    this.rolesUpdated.next();
  }
}
